import { getContentVendorId, getDefaultVendorId } from './environment';

// Mutable bridge between the stores (which own credentials) and the clients (which
// consume them). Keeps api/ free of any import from stores/ - no circular graph.
// Stores push on every change; clients read per request.

export interface QltCredentials {
  jwt: string | null;
  userId: string | number | null;
  // false for a board QR session: its jwt is short-lived on purpose, so a 401 signs out instead of calling /refreshToken
  renewable: boolean;
}

export interface IzteachCredentials {
  accessToken: string | null;
  refreshToken: string | null;
  userId: string | null;
  vendorId: string;
}

export const qltSession: QltCredentials = { jwt: null, userId: null, renewable: true };

export const izteachSession: IzteachCredentials = {
  accessToken: null,
  refreshToken: null,
  userId: null,
  vendorId: getDefaultVendorId(),
};

export interface SessionHooks {
  // Called when the QLT jwt could not be renewed. The auth store wires sign-out here.
  onQltAuthFailure: (message: string) => void;
  // Called when a refresh produced a new jwt, so the store can persist it.
  onQltTokenRenewed: (jwt: string) => void;
  onQltSignedOut: () => void;
  // IZTeach credentials are minted by the qlt-signin exchange, so "recovery" means
  // running that exchange again. The session store wires it; returns true on success.
  reauthorizeIzteach: () => Promise<boolean>;
  onIzteachAuthFailure: (message: string) => void;
}

export const sessionHooks: SessionHooks = {
  onQltAuthFailure: () => undefined,
  onQltTokenRenewed: () => undefined,
  onQltSignedOut: () => undefined,
  reauthorizeIzteach: () => Promise.resolve(false),
  onIzteachAuthFailure: () => undefined,
};

export function setQltCredentials(jwt: string | null, userId: string | number | null, renewable = true): void {
  qltSession.jwt = jwt;
  qltSession.userId = userId;
  qltSession.renewable = renewable;
}

export function clearQltCredentials(): void {
  setQltCredentials(null, null);
}

export function setIzteachCredentials(next: Partial<IzteachCredentials>): void {
  if (next.accessToken !== undefined) izteachSession.accessToken = next.accessToken;
  if (next.refreshToken !== undefined) izteachSession.refreshToken = next.refreshToken;
  if (next.userId !== undefined) izteachSession.userId = next.userId;
  if (next.vendorId !== undefined) izteachSession.vendorId = next.vendorId;
}

// The tenant the kho screens list (courses, documents): the configured library tenant, else the session's.
export function libraryVendorId(): string {
  return getContentVendorId() || izteachSession.vendorId;
}

export function clearIzteachCredentials(): void {
  izteachSession.accessToken = null;
  izteachSession.refreshToken = null;
  izteachSession.userId = null;
}
